import { login, logout, getInfo } from '@/api/common'
import {
  clearLegacyDingLoginState,
  clearUserSessionStorage,
  getToken,
  removeToken,
  setToken
} from '@/utils/auth'
import { isInDingTalk, requestDingAuthCode } from '@/utils/dingAuth'

let bootstrapSessionPromise = null
let bootstrapAttemptedThisBoot = false
let dingSessionPromise = null
let dingAttemptedThisBoot = false
let dingLoginBlockedThisBoot = false
let manualLogoutThisBoot = false

function createLoginError(response) {
  const error = new Error((response && response.message) || '登录失败')
  error.code = response && response.code
  error.responseData = response
  return error
}

function getErrorCode(error) {
  const response = error && error.response
  const responseData = response && response.data
  return Number(
    (error && error.code) ||
      (error && error.responseData && error.responseData.code) ||
      (responseData && responseData.code) ||
      (response && response.status) ||
      0
  )
}

function isSessionInvalidError(error) {
  return [401, 50000].includes(getErrorCode(error))
}

function clearLocalSession(commit, preserveRedirect = false) {
  removeToken()
  clearUserSessionStorage(preserveRedirect)
  commit('RESET_STATE')
}

function getCurrentSessionResult(state) {
  if (state.token && state.userInfo && state.userInfo.id) {
    return {
      status: 'authenticated',
      userInfo: state.userInfo
    }
  }
  return { status: 'unauthenticated' }
}

const getDefaultState = () => {
  return {
    token: getToken(),
    name: '',
    avatar: '',
    userInfo: {},
    loginUserId: null
  }
}

const state = getDefaultState()

const mutations = {
  RESET_STATE: state => {
    Object.assign(state, getDefaultState())
  },
  SET_TOKEN: (state, token) => {
    state.token = token
  },
  SET_NAME: (state, name) => {
    state.name = name
  },
  SET_AVATAR: (state, avatar) => {
    state.avatar = avatar
  },
  SET_USERINFO: (state, userInfo) => {
    state.userInfo = userInfo
  },
  SET_LOGINUSERID: (state, loginUserId) => {
    state.loginUserId = loginUserId
  }
}

const actions = {
  // 用户登录
  login({ commit }, payload) {
    const credentials = payload.credentials
    const loginMethod = payload.loginMethod

    return login(credentials).then(response => {
      if (
        !response ||
        response.code !== 200 ||
        !response.data ||
        !response.data.access_token
      ) {
        return Promise.reject(createLoginError(response))
      }

      const { data } = response
      setToken(data.access_token, data.expires_in)
      commit('SET_TOKEN', data.access_token)
      manualLogoutThisBoot = false
      dingLoginBlockedThisBoot = false
      return {
        ...data,
        loginMethod
      }
    })
  },

  // 获取用户信息，并使用 Redis 实际剩余 TTL 静默校准本地 H5 Token。
  getInfo({ commit }, options = {}) {
    const requestOptions = options.skipSessionRecovery
      ? { skipSessionRecovery: true }
      : {}

    return getInfo(undefined, requestOptions).then(response => {
      if (!response || response.code !== 200 || !response.data) {
        return Promise.reject(createLoginError(response))
      }

      const { h5_session: h5Session, ...userInfo } = response.data
      if (
        !h5Session ||
        !h5Session.access_token ||
        Number(h5Session.expires_in) <= 0
      ) {
        return Promise.reject(
          createLoginError({
            code: 50000,
            message: '服务端未返回有效的 H5 会话信息'
          })
        )
      }

      // h5_session 只用于更新 Cookie，禁止写入 localStorage 扩大 Token 暴露范围。
      setToken(h5Session.access_token, h5Session.expires_in)
      commit('SET_TOKEN', h5Session.access_token)
      localStorage.setItem('loginUserInfo', JSON.stringify(userInfo))
      commit('SET_USERINFO', userInfo)
      commit('SET_NAME', userInfo.realname)
      commit('SET_AVATAR', userInfo.img || userInfo.thumb_img)
      commit('SET_LOGINUSERID', userInfo.id)
      return userInfo
    })
  },

  /**
   * H5 完整加载只执行一次启动认证：优先校验本地 Token，失效时才在钉钉环境兜底。
   */
  bootstrapSession({ commit, dispatch, state }) {
    if (bootstrapSessionPromise) {
      return bootstrapSessionPromise
    }
    if (bootstrapAttemptedThisBoot) {
      return Promise.resolve(getCurrentSessionResult(state))
    }

    bootstrapAttemptedThisBoot = true
    clearLegacyDingLoginState()
    bootstrapSessionPromise = Promise.resolve()
      .then(() => {
        if (state.token) {
          return dispatch('getInfo', { skipSessionRecovery: true })
            .then(userInfo => ({
              status: 'authenticated',
              userInfo
            }))
            .catch(error => {
              if (
                isSessionInvalidError(error) &&
                isInDingTalk() &&
                !manualLogoutThisBoot
              ) {
                return dispatch('recoverDingSession')
              }

              clearLocalSession(commit, true)
              if (isSessionInvalidError(error)) {
                return { status: 'unauthenticated' }
              }
              return Promise.reject(error)
            })
        }

        if (isInDingTalk() && !manualLogoutThisBoot) {
          return dispatch('ensureDingSession')
        }
        return { status: 'unauthenticated' }
      })
      .finally(() => {
        bootstrapSessionPromise = null
      })

    return bootstrapSessionPromise
  },

  /**
   * 同一页面生命周期内最多执行一次钉钉认证；force 仅用于当前 Token 失效后的恢复。
   */
  ensureDingSession({ dispatch }, options = {}) {
    const force = Boolean(options.force)
    if (!isInDingTalk() || manualLogoutThisBoot || dingLoginBlockedThisBoot) {
      return Promise.resolve({ status: 'not_in_dingtalk' })
    }
    if (dingSessionPromise) {
      return dingSessionPromise
    }
    if (dingAttemptedThisBoot && !force) {
      return Promise.resolve({ status: 'already_attempted' })
    }

    dingAttemptedThisBoot = true
    dingSessionPromise = requestDingAuthCode()
      .then(dingCode =>
        dispatch('login', {
          credentials: {
            username: '',
            password: '',
            dingCode
          },
          loginMethod: 'ding'
        })
      )
      .then(() => dispatch('getInfo', { skipSessionRecovery: true }))
      .then(userInfo => ({
        status: 'authenticated',
        userInfo
      }))
      .catch(error => {
        // 认证失败后阻止本次页面生命周期继续拉起钉钉，避免失效请求形成授权循环。
        dingLoginBlockedThisBoot = true
        return dispatch('resetToken', { preserveRedirect: true }).then(() => Promise.reject(error))
      })
      .finally(() => {
        dingSessionPromise = null
      })

    return dingSessionPromise
  },

  /**
   * 业务请求 Token 失效后，仅在钉钉环境通过 Code 恢复当前 H5 会话。
   */
  recoverDingSession({ commit, dispatch }) {
    clearLocalSession(commit, true)
    if (!isInDingTalk() || manualLogoutThisBoot || dingLoginBlockedThisBoot) {
      return Promise.resolve({ status: 'not_in_dingtalk' })
    }
    return dispatch('ensureDingSession', { force: true })
  },

  // 退出登录
  logout({ commit, state }) {
    manualLogoutThisBoot = true
    dingLoginBlockedThisBoot = true
    bootstrapAttemptedThisBoot = true
    return logout(state.token)
      .then(response => {
        if (!response || response.code !== 200) {
          return Promise.reject(createLoginError(response))
        }
        return response
      })
      .finally(() => {
        // 当前 SPA 生命周期内不再自动拉起钉钉；完整刷新后内存状态会自然重置。
        clearLocalSession(commit)
      })
  },

  // 清除本地登录状态，不改变本次启动是否已尝试钉钉认证的记录。
  resetToken({ commit }, options = {}) {
    clearLocalSession(commit, Boolean(options.preserveRedirect))
    return Promise.resolve()
  }
}

export default {
  namespaced: true,
  state,
  actions,
  mutations
}
